import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { API_DOCS } from './AssociazioniContiPage.api';

const MODULO = 'amministrazione.associazioni_conti';

type Tipo = 'CLIENTE' | 'FORNITORE';
type Area = 'ITALIA' | 'ESTERO';

interface Sottoconto { id: number; codice: string; descrizione: string }
interface ContoRef { id: number; codice: string; descrizione: string; attivo?: boolean }
interface Assoc { tipoSoggetto: Tipo; categoria: string; area: Area; contoCogeId: number; conto: ContoRef | null }
interface DataResp {
  associazioni: Assoc[];
  sottocontiCliente: Sottoconto[];
  sottocontiFornitore: Sottoconto[];
}

const CATEGORIE: Record<Tipo, { value: string; label: string }[]> = {
  CLIENTE: [
    { value: 'CATENA_GDO', label: 'Catena GDO' },
    { value: 'AZIENDA_B2B', label: 'Azienda B2B' },
    { value: 'PRIVATO_B2C', label: 'Privato B2C' },
    { value: 'AGENTE', label: 'Agente' },
  ],
  FORNITORE: [
    { value: 'FORNITORE_MERCE', label: 'Fornitore Merce' },
    { value: 'SUBAPPALTATORE', label: 'Subappaltatore' },
    { value: 'FORNITORE_SERVIZI', label: 'Fornitore Servizi' },
    { value: 'AGENTE', label: 'Agente' },
  ],
};

const AREE: { value: Area; label: string }[] = [
  { value: 'ITALIA', label: 'Italia' },
  { value: 'ESTERO', label: 'Estero' },
];

const keyOf = (t: Tipo, c: string, a: Area) => `${t}|${c}|${a}`;

export default function AssociazioniContiPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [data, setData] = useState<DataResp | null>(null);
  const [assocMap, setAssocMap] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = async () => {
    if (!societaId) { setData(null); setAssocMap({}); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/coge/associazioni-conti?societaId= -> { success: true, data: { associazioni: [{ tipoSoggetto, categoria, area, contoCogeId, conto }], sottocontiCliente: [{ id, codice, descrizione }], sottocontiFornitore: [{ id, codice, descrizione }] } }
      const res = await apiFetch<{ success: boolean; data: DataResp }>(
        `/api/coge/associazioni-conti?societaId=${societaId}`,
      );
      setData(res.data);
      const map: Record<string, number> = {};
      for (const a of res.data.associazioni) map[keyOf(a.tipoSoggetto, a.categoria, a.area)] = a.contoCogeId;
      setAssocMap(map);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  // Mappa id → conto per mostrare l'etichetta di un valore non più presente in lista
  const contiById = useMemo(() => {
    const m: Record<number, ContoRef> = {};
    if (data) {
      for (const s of data.sottocontiCliente) m[s.id] = s;
      for (const s of data.sottocontiFornitore) m[s.id] = s;
      for (const a of data.associazioni) if (a.conto) m[a.conto.id] = a.conto;
    }
    return m;
  }, [data]);

  const onChangeConto = async (tipo: Tipo, categoria: string, area: Area, value: string) => {
    if (!societaId) return;
    const k = keyOf(tipo, categoria, area);
    const contoCogeId = value ? Number(value) : null;
    setSavingKey(k);
    setError('');
    try {
      // @api PUT /api/coge/associazioni-conti { societaId, tipoSoggetto, categoria, area, contoCogeId } -> { success: true }
      await apiFetch('/api/coge/associazioni-conti', {
        method: 'PUT',
        body: JSON.stringify({ societaId: Number(societaId), tipoSoggetto: tipo, categoria, area, contoCogeId }),
      });
      setAssocMap(prev => {
        const next = { ...prev };
        if (contoCogeId == null) delete next[k]; else next[k] = contoCogeId;
        return next;
      });
    } catch (e: any) {
      setError(e.message || 'Errore nel salvataggio');
      load(); // riallinea in caso di errore
    } finally {
      setSavingKey(null);
    }
  };

  const renderSezione = (tipo: Tipo, sottoconti: Sottoconto[]) => {
    const titolo = tipo === 'CLIENTE' ? 'Clienti' : 'Fornitori';
    return (
      <div className="bop-card">
        <div className="bop-card__header">
          <h3 className="bop-card__title">{titolo}</h3>
        </div>
        <div className="bop-card__body">
          {sottoconti.length === 0 ? (
            <div className="bop-empty-state">
              Nessun sottoconto di Stato Patrimoniale con sottotipo {tipo} disponibile per questa società.
              Crealo prima nel Piano dei conti CO.GE.
            </div>
          ) : (
            <table className="bop-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Conto Italia</th>
                  <th>Conto Estero</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIE[tipo].map(cat => (
                  <tr key={cat.value}>
                    <td>{cat.label}</td>
                    {AREE.map(area => {
                      const k = keyOf(tipo, cat.value, area.value);
                      const sel = assocMap[k];
                      const fuoriLista = sel != null && !sottoconti.some(s => s.id === sel);
                      return (
                        <td key={area.value}>
                          <select
                            className="bop-select"
                            value={sel != null ? String(sel) : ''}
                            disabled={!canEdit || savingKey === k}
                            onChange={e => onChangeConto(tipo, cat.value, area.value, e.target.value)}
                          >
                            <option value="">— Non associato —</option>
                            {fuoriLista && contiById[sel] && (
                              <option value={String(sel)}>
                                {contiById[sel].codice} — {contiById[sel].descrizione} (non più valido)
                              </option>
                            )}
                            {sottoconti.map(s => (
                              <option key={s.id} value={String(s.id)}>{s.codice} — {s.descrizione}</option>
                            ))}
                          </select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const noSocieta = !societaId;

  return (
    <Layout>
      <div className="associazioni-conti">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Associazioni conti</h2>
            <p>Sottoconto CO.GE. di Stato Patrimoniale associato a ogni categoria di cliente/fornitore, distinto per Italia ed Estero.</p>
          </div>
        </div>

        <div className="bop-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        {noSocieta ? (
          <div className="bop-card">
            <div className="bop-card__body">
              <div className="bop-empty-state">Seleziona una società per configurare le associazioni conti.</div>
            </div>
          </div>
        ) : loading ? (
          <div className="bop-card">
            <div className="bop-card__body">
              <div className="bop-empty-state">Caricamento…</div>
            </div>
          </div>
        ) : data ? (
          <>
            {renderSezione('CLIENTE', data.sottocontiCliente)}
            {renderSezione('FORNITORE', data.sottocontiFornitore)}
          </>
        ) : null}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
